import { c as _c } from "react/compiler-runtime";
/**
 * Next.js dev-indicator section of the DevPill. Extracted from
 * dev-pill.tsx as part of Phase 6 of the round-3 enactment plan
 * (review-9016e0cc-83b #7). Only consumed by dev-pill.tsx's PillUI.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import type { NextjsAdapter } from '../adapters';
export interface NextjsSectionState {
  visible: boolean;
  hasError: boolean;
  toggle: () => void;
}

/** Polls the Next.js adapter every 2 s for error state and exposes a toggle for showing/hiding the native indicator. */
export function useNextjsSection(adapter, ready) {
  const $ = _c(10);
  const [visible, setVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  let t0;
  let t1;
  if ($[0] !== adapter || $[1] !== ready) {
    t0 = () => {
      if (!ready) {
        return;
      }
      const poll = function poll() {
        setHasError(adapter.hasError());
      };
      poll();
      const id = setInterval(poll, 2000);
      return () => clearInterval(id);
    };
    t1 = [ready, adapter];
    $[0] = adapter;
    $[1] = ready;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== adapter) {
    t2 = () => {
      if (adapter.isVisible()) {
        adapter.hide();
        setVisible(false);
      } else {
        adapter.show();
        setVisible(true);
      }
    };
    $[4] = adapter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const toggle = t2;
  let t3;
  if ($[6] !== hasError || $[7] !== toggle || $[8] !== visible) {
    t3 = {
      visible,
      hasError,
      toggle
    };
    $[6] = hasError;
    $[7] = toggle;
    $[8] = visible;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/** Renders the Next.js "N" logotype SVG at 14×14 px for use in the pill section button. */
function NextjsLogo() {
  const $ = _c(10);
  const maskId = useId();
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <circle cx="90" cy="90" r="90" fill="white" />;
    t1 = <path d="M149.508 157.52L69.142 54H54v72.13h12V69.3l73.7 95.8a90.1 90.1 0 009.808-7.58z" fill="black" />;
    t2 = <path d="M115 54h12v72h-12z" fill="black" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== maskId) {
    t3 = <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="180" height="180">{t0}{t1}{t2}</mask>;
    $[3] = maskId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `url(#${maskId})`;
  let t5;
  if ($[5] !== t4) {
    t5 = <circle cx="90" cy="90" r="90" fill="white" mask={t4} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <svg width="14" height="14" viewBox="0 0 180 180" fill="none">{t3}{t5}</svg>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/** Renders the Next.js indicator pill button, applying red/green CSS classes based on error state. */
export function NextjsSection(t0) {
  const $ = _c(4);
  const {
    section
  } = t0;
  const t1 = `section nextjs ${section.hasError ? "nextjs-err" : "nextjs-ok"}${section.visible ? " active" : ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NextjsLogo />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== section.toggle || $[2] !== t1) {
    t3 = <button type="button" className={t1} onClick={section.toggle}>{t2}</button>;
    $[1] = section.toggle;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}